/*
 * Copyright Mission 42 Ventures, Inc. All rights reserved.
 * Unauthorized copying, distribution, or use is prohibited.
 */

import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Box, Button, Checkbox, CircularProgress, Dialog, FormControlLabel, FormHelperText,
  IconButton, Link, Popper, Radio, RadioGroup, TextField, Typography, makeStyles,
} from '@material-ui/core';
import { alpha } from '@material-ui/core/styles';
import { Alert, Autocomplete, createFilterOptions } from '@material-ui/lab';
import CloseIcon from '@material-ui/icons/Close';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { CLIENT_LABELS, MCP_CLIENT_IDS, type McpClientId } from '../mcpConnect/types';
import { OpensInNewTab } from './OpensInNewTab';
import {
  EMPTY_FIELDS, LOCAL_ARG, LOCAL_COMMAND, NAME_RE, PLAIN_HTTPS_URL, ProposalError, fieldsFromYaml, githubPr,
  proposalFields, useProposalsApi,
  type EntryFields, type ProposalInput,
} from './proposals';

type Target = { mode: 'add' } | { mode: 'edit'; name: string } | null;
type FieldKey = keyof EntryFields | 'name' | 'testedClients';
type Errors = Partial<Record<FieldKey, string>>;
type Banner = { kind: 'exists' | 'stale' | 'pending' | 'budget' | 'github' | 'form' | 'load'; message: string; detail?: string; prUrl?: string; prNumber?: number } | null;
type Done = { status: 'created' | 'updated'; prUrl: string; prNumber: number } | null;
type TeamOption = { value: string; name: string; label: string; org?: string };
const filterTeams = createFilterOptions<TeamOption>({
  stringify: option => `${option.org ?? ''} ${option.name} ${option.label}`,
});
const sortTeams = (a: TeamOption, b: TeamOption) =>
  (a.org ?? '').localeCompare(b.org ?? '') || a.label.localeCompare(b.label);
const OwnerTeamPopper = (props: React.ComponentProps<typeof Popper>) => {
  const anchor = typeof props.anchorEl === 'function' ? props.anchorEl() : props.anchorEl;
  const anchorWidth = anchor && 'clientWidth' in anchor ? anchor.clientWidth : 0;
  return <Popper {...props} placement="bottom-start" style={{ ...props.style, width: 'auto', minWidth: Math.min(Math.max(anchorWidth, 360), 420), maxWidth: 420 }} />;
};
const FIELD_KEYS: FieldKey[] = ['name', 'title', 'description', 'provider', 'transport', 'url', 'command', 'args', 'docsUrl', 'tags', 'owner', 'lifecycle', 'testedClients'];
const isFieldKey = (field: string): field is FieldKey => FIELD_KEYS.some(key => key === field);
const useStyles = makeStyles(theme => ({
  paper: { borderRadius: 12, padding: theme.spacing(3), backgroundImage: 'none', [theme.breakpoints.down('xs')]: { padding: theme.spacing(2) } },
  backdrop: { backdropFilter: 'blur(6px)', backgroundColor: alpha(theme.palette.common.black, 0.5) },
  head: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: theme.spacing(1), marginBottom: theme.spacing(2) },
  form: { display: 'grid', gap: theme.spacing(2) },
  row: { display: 'grid', gap: theme.spacing(2), gridTemplateColumns: '1fr 1fr', [theme.breakpoints.down('xs')]: { gridTemplateColumns: '1fr' } },
  clients: { display: 'grid', gap: theme.spacing(0.5) },
  attest: { fontSize: '0.75rem', color: theme.palette.text.secondary },
  preview: { fontSize: '0.8125rem', '& summary': { cursor: 'pointer', fontWeight: 500 }, '& pre': { overflowX: 'auto', fontSize: '0.75rem', padding: theme.spacing(1.5), borderRadius: 6, backgroundColor: theme.palette.type === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)' } },
  actions: { display: 'flex', justifyContent: 'flex-end', gap: theme.spacing(1), marginTop: theme.spacing(1) },
}));

export const McpEntryDialog = ({ target, existingNames, onClose, onSwitchToEdit, onSubmitted }: {
  target: Target; existingNames: string[]; onClose: () => void; onSwitchToEdit: (name: string) => void; onSubmitted?: () => void;
}) => {
  const classes = useStyles();
  const api = useProposalsApi();
  const catalogApi = useApi(catalogApiRef);
  const titleId = useId();
  const [name, setName] = useState('');
  const [fields, setFields] = useState<EntryFields>(EMPTY_FIELDS);
  const [tagsText, setTagsText] = useState('');
  const [argsText, setArgsText] = useState('');
  const [tested, setTested] = useState<McpClientId[]>([]);
  const [previouslyTested, setPreviouslyTested] = useState<McpClientId[]>([]);
  const [baseSha, setBaseSha] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [teams, setTeams] = useState<TeamOption[]>([]);
  const [teamsLoading, setTeamsLoading] = useState(false);
  const [teamsFailed, setTeamsFailed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const requestGeneration = useRef(0);
  const [touched, setTouched] = useState<Partial<Record<FieldKey, boolean>>>({});
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<Errors>({});
  const [banner, setBanner] = useState<Banner>(null);
  const [done, setDone] = useState<Done>(null);
  const prLink = done ? githubPr(done.prUrl) : undefined;
  const [previewOpen, setPreviewOpen] = useState(false);
  const [preview, setPreview] = useState<Array<{ path: string; content: string }> | null>(null);
  const editing = target?.mode === 'edit';
  const editName = target?.mode === 'edit' ? target.name : undefined;

  const load = useCallback(async () => {
    if (!editName) return;
    const generation = ++requestGeneration.current;
    setLoading(true);
    setBanner(null);
    try {
      const { raw, sha, pendingPr } = await api.getEntry(editName);
      const parsed = fieldsFromYaml(raw);
      if (generation !== requestGeneration.current) return;
      setName(parsed.name);
      setFields(parsed.fields);
      setTagsText(parsed.fields.tags.join(', '));
      setArgsText(parsed.fields.args.join('\n'));
      setTested(parsed.testedClients);
      setPreviouslyTested(parsed.testedClients);
      setBaseSha(sha);
      if (pendingPr) setBanner({ kind: 'pending', message: `An open pull request already changes this entry — review, merge or close #${pendingPr.number} first`, prNumber: pendingPr.number, prUrl: pendingPr.url });
      setTouched({});
      setAttempted(false);
      setServerErrors({});
    } catch (e) {
      if (generation !== requestGeneration.current) return;
      setBanner({ kind: 'load', message: e instanceof ProposalError ? e.message : 'Couldn’t load this entry', detail: e instanceof ProposalError ? e.body.detail : String(e) });
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, [api, editName]);

  useEffect(() => {
    ++requestGeneration.current;
    if (!target) return;
    submittingRef.current = false;
    setSubmitting(false);
    setLoading(false);
    setName('');
    setFields(EMPTY_FIELDS);
    setTagsText('');
    setArgsText('');
    setTested([]);
    setPreviouslyTested([]);
    setBaseSha(undefined);
    setTouched({});
    setAttempted(false);
    setServerErrors({});
    setBanner(null);
    setDone(null);
    setPreview(null);
    setPreviewOpen(false);
    if (editName) load();
  }, [target?.mode, editName, load]);

  useEffect(() => {
    if (!target) return undefined;
    let active = true;
    setTeamsLoading(true);
    setTeamsFailed(false);
    catalogApi.getEntities({
      filter: { kind: 'Group' },
      fields: ['metadata.name', 'metadata.namespace', 'metadata.title', 'spec.profile.displayName', 'metadata.annotations'],
    }).then(({ items }) => {
      if (!active) return;
      setTeams(items.map(group => {
        const name = group.metadata.name;
        const namespace = group.metadata.namespace ?? 'default';
        const annotations = group.metadata.annotations ?? {};
        const teamSlug = annotations['github.com/team-slug'];
        const location = annotations['backstage.io/managed-by-location'];
        const org = (teamSlug?.includes('/') ? teamSlug.split('/')[0] : undefined)
          ?? location?.match(/github\.com\/(?:orgs\/)?([^/]+)/)?.[1]
          ?? namespace;
        const profile = group.spec?.profile as { displayName?: string } | undefined;
        return { value: namespace === 'default' ? name : `${namespace}/${name}`,
          name, label: profile?.displayName ?? group.metadata.title ?? name, org };
      }).sort(sortTeams));
    }).catch(() => {
      if (active) setTeamsFailed(true);
    }).finally(() => {
      if (active) setTeamsLoading(false);
    });
    return () => { active = false; };
  }, [catalogApi, target?.mode, editName]);

  const ownerOptions = useMemo(() => fields.owner && !teams.some(team => team.value === fields.owner)
    ? [...teams, { value: fields.owner, name: fields.owner, label: fields.owner }].sort(sortTeams)
    : teams, [fields.owner, teams]);

  const input: ProposalInput = useMemo(() => ({
    mode: editing ? 'edit' : 'add', name, testedClients: tested,
    attestedClients: editing ? tested.filter(client => !previouslyTested.includes(client)) : tested, baseSha,
    fields: proposalFields({
      ...fields,
      tags: tagsText.split(',').map(t => t.trim()).filter(Boolean),
      args: argsText.split('\n').map(a => a.trim()).filter(Boolean),
    }),
  }), [editing, name, tested, previouslyTested, baseSha, fields, tagsText, argsText]);

  const clientErrors: Errors = useMemo(() => {
    const errors: Errors = {};
    if (!name) errors.name = 'Name is required';
    else if (!NAME_RE.test(name)) errors.name = 'Use lowercase letters, digits and single hyphens';
    else if (!editing && existingNames.includes(name)) errors.name = 'Already in the catalog — edit it instead';
    if (!fields.title.trim()) errors.title = 'Title is required';
    if (!fields.provider.trim()) errors.provider = 'Who runs the server is required';
    if (!fields.owner.trim()) errors.owner = 'Choose the owning team';
    if (fields.transport === 'local') {
      const args = argsText.split('\n').map(a => a.trim()).filter(Boolean);
      if (!fields.command.trim()) errors.command = 'Command is required';
      else if (!LOCAL_COMMAND.test(fields.command)) errors.command = 'A bare program name such as npx or snyk: lowercase letters, digits and . _ - only, no path';
      if (args.length === 0) errors.args = 'At least one argument, one per line';
      else if (args.length > 30 || args.some(a => !LOCAL_ARG.test(a))) errors.args = 'One argument per line (at most 30), each letters, digits and . _ @ = : / + - only — no spaces, quotes or shell characters';
    } else if (!fields.url.trim()) errors.url = 'Remote URL is required';
    else if (!PLAIN_HTTPS_URL.test(fields.url)) errors.url = 'Use a plain https URL: letters, digits and . _ ~ % / - only, no query or fragment';
    if (tested.length === 0) errors.testedClients = 'Tick at least one client you tested end to end';
    return errors;
  }, [name, editing, existingNames, fields.title, fields.provider, fields.owner, fields.transport, fields.url, fields.command, argsText, tested]);
  const valid = Object.keys(clientErrors).length === 0 && (!editing || Boolean(baseSha)) && banner?.kind !== 'pending';
  const errorFor = (key: FieldKey) => serverErrors[key] ??
    (clientErrors[key] && (attempted || touched[key] || (key === 'name' && existingNames.includes(name))) ? clientErrors[key] : undefined);
  const touch = (key: FieldKey) => setTouched(previous => ({ ...previous, [key]: true }));
  const clearServerError = (key: FieldKey) => setServerErrors(previous => ({ ...previous, [key]: undefined }));
  const set = (key: keyof EntryFields) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFields(previous => ({ ...previous, [key]: event.target.value }));
    touch(key);
    clearServerError(key);
  };

  useEffect(() => {
    if (!previewOpen || !valid) return undefined;
    let active = true;
    const timer = setTimeout(() => {
      api.preview(input).then(result => {
        if (active) setPreview(result.files);
      }).catch(e => {
        if (!active) return;
        if (e instanceof ProposalError && e.status === 400 && e.body.field && isFieldKey(e.body.field)) {
          setServerErrors({ [e.body.field]: e.body.message ?? e.message });
        } else if (e instanceof ProposalError && e.status !== 429) {
          setBanner({ kind: 'form', message: e.message });
        }
      });
    }, 400);
    return () => { active = false; clearTimeout(timer); };
  }, [previewOpen, valid, input, api]);

  const submit = async () => {
    setAttempted(true);
    if (!valid || submittingRef.current) return;
    const generation = ++requestGeneration.current;
    submittingRef.current = true;
    setSubmitting(true);
    setBanner(null);
    setServerErrors({});
    try {
      const result = await api.submit(input);
      onSubmitted?.();
      if (generation === requestGeneration.current) setDone(result);
    } catch (e) {
      if (generation !== requestGeneration.current) return;
      if (!(e instanceof ProposalError)) {
        setBanner({ kind: 'github', message: 'Couldn’t reach Backstage', detail: String(e) });
        return;
      }
      const { error, field, message = e.message, detail } = e.body;
      if (e.status === 400 && field && isFieldKey(field)) setServerErrors({ [field]: message });
      else if (e.status === 409 && error === 'exists') setBanner({ kind: 'exists', message });
      else if (e.status === 409 && error === 'stale') setBanner({ kind: 'stale', message });
      else if (e.status === 409 && error === 'pending') setBanner({ kind: 'pending', message, prNumber: e.body.prNumber, prUrl: e.body.prUrl });
      else if (e.status === 429) setBanner({ kind: 'budget', message });
      else if (e.status === 401) setBanner({ kind: 'form', message: 'Your session expired — sign in again' });
      else if (e.status === 403) setBanner({ kind: 'form', message: "You can't propose MCP servers from this account" });
      else if (e.status === 404) setBanner({ kind: 'form', message: 'Proposals are turned off right now' });
      else if (e.status === 502) setBanner({ kind: 'github', message: 'Couldn’t reach GitHub', detail: detail ?? message });
      else setBanner({ kind: 'form', message });
    } finally {
      if (generation === requestGeneration.current) {
        submittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  const heading = editing ? `Edit ${fields.title || editName}` : 'Add an MCP server';
  return (
    <Dialog open={target !== null} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby={titleId}
      classes={{ paper: classes.paper }} BackdropProps={{ className: classes.backdrop }}>
      <Box className={classes.head}>
        <Typography component="h2" variant="h5" id={titleId}>{heading}</Typography>
        <IconButton size="small" aria-label="Close" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
      </Box>
      {done ? (
        <Box>
          <Typography variant="h6">{done.status === 'updated' ? 'Updated the open pull request' : 'Pull request opened'}</Typography>
          <Typography>
            {/* Only a github.com pull-request URL becomes a link; anything else from the server is shown as text. */}
            {prLink ? (
              <>
                <Link color="primary" underline="always" href={prLink.href} target="_blank" rel="noopener noreferrer">#{done.prNumber}<OpensInNewTab /></Link> in {prLink.repo}.
              </>
            ) : (
              <>#{done.prNumber} in the catalog repo.</>
            )}
            {' '}It appears on this page after the PR is reviewed and merged.
          </Typography>
          <Box className={classes.actions}>
            {prLink && <Button variant="outlined" color="primary" href={prLink.href} target="_blank" rel="noopener noreferrer">View pull request<OpensInNewTab /></Button>}
            <Button variant="contained" color="primary" onClick={onClose}>Close</Button>
          </Box>
        </Box>
      ) : loading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress aria-label="Loading entry" /></Box>
      ) : (
        <form noValidate className={classes.form} onSubmit={(event: React.FormEvent) => { event.preventDefault(); submit(); }}>
          {banner && (
            <Alert severity={banner.kind === 'budget' || banner.kind === 'pending' ? 'warning' : 'error'} role="alert" action={
              banner.kind === 'exists' ? <Button size="small" onClick={() => onSwitchToEdit(name)}>Edit it</Button>
              : banner.kind === 'stale' ? <Button size="small" onClick={load}>Reload</Button>
              : banner.kind === 'load' ? <Button size="small" onClick={load}>Retry</Button>
              : banner.kind === 'github' ? <Button size="small" onClick={submit}>Retry</Button>
              : undefined
            }>
              {banner.message}
              {banner.kind === 'pending' && banner.prUrl && githubPr(banner.prUrl) && (
                <> <Link color="primary" underline="always" href={githubPr(banner.prUrl)!.href} target="_blank" rel="noopener noreferrer">Review #{banner.prNumber}<OpensInNewTab /></Link></>
              )}
              {banner.detail && <details><summary>Details</summary>{banner.detail}</details>}
            </Alert>
          )}
          <TextField id="mcp-entry-name" label="Name" variant="outlined" value={name} autoFocus={!editing} required InputProps={{ readOnly: editing }}
            onChange={event => { setName(event.target.value); touch('name'); clearServerError('name'); }} onBlur={() => touch('name')}
            error={Boolean(errorFor('name'))} helperText={errorFor('name') ?? 'The install name in every client command, e.g. acme'} />
          {!editing && existingNames.includes(name) && <Box><Button size="small" onClick={() => onSwitchToEdit(name)}>Edit it</Button></Box>}
          <div className={classes.row}>
            <TextField id="mcp-entry-title" label="Title" variant="outlined" value={fields.title} onChange={set('title')} onBlur={() => touch('title')} required autoFocus={editing}
              inputProps={{ maxLength: 100 }} error={Boolean(errorFor('title'))} helperText={errorFor('title')} />
            <TextField id="mcp-entry-provider" label="Who runs the server" variant="outlined" value={fields.provider} onChange={set('provider')} onBlur={() => touch('provider')} required
              inputProps={{ maxLength: 80 }} error={Boolean(errorFor('provider'))} helperText={errorFor('provider')} />
          </div>
          <TextField id="mcp-entry-description" label="Description" variant="outlined" value={fields.description} onChange={set('description')} onBlur={() => touch('description')}
            inputProps={{ maxLength: 200 }} error={Boolean(errorFor('description'))} helperText={errorFor('description') ?? 'One line shown on the card'} />
          <RadioGroup row aria-label="Transport" value={fields.transport}
            onChange={(_event, value) => { setFields(previous => ({ ...previous, transport: value as EntryFields['transport'] })); clearServerError('transport'); }}>
            <FormControlLabel value="remote" control={<Radio />} label="Remote server" />
            <FormControlLabel value="local" control={<Radio />} label="Local command" />
          </RadioGroup>
          {errorFor('transport') && <FormHelperText error>{errorFor('transport')}</FormHelperText>}
          {fields.transport === 'local' ? (
            <div className={classes.row}>
              <TextField id="mcp-entry-command" label="Command" variant="outlined" value={fields.command} onChange={set('command')} onBlur={() => touch('command')} required
                error={Boolean(errorFor('command'))} helperText={errorFor('command') ?? 'The program the client runs, e.g. npx — no path, no shell'} />
              <TextField id="mcp-entry-args" label="Arguments" variant="outlined" value={argsText} multiline minRows={3} required
                onChange={event => { setArgsText(event.target.value); touch('args'); clearServerError('args'); }} onBlur={() => touch('args')}
                error={Boolean(errorFor('args'))} helperText={errorFor('args') ?? 'One per line, e.g. -y, snyk@latest, mcp, -t, stdio'} />
            </div>
          ) : (
            <TextField id="mcp-entry-url" label="Remote URL" variant="outlined" value={fields.url} onChange={set('url')} onBlur={() => touch('url')} required
              error={Boolean(errorFor('url'))} helperText={errorFor('url') ?? 'The streamable-http endpoint, e.g. https://mcp.example.com/mcp'} />
          )}
          <TextField id="mcp-entry-docs-url" label="Docs link" variant="outlined" value={fields.docsUrl} onChange={set('docsUrl')} onBlur={() => touch('docsUrl')}
            error={Boolean(errorFor('docsUrl'))} helperText={errorFor('docsUrl') ?? 'Optional, https'} />
          <div className={classes.row}>
            <TextField id="mcp-entry-tags" label="Tags" variant="outlined" value={tagsText} onChange={event => { setTagsText(event.target.value); touch('tags'); clearServerError('tags'); }} onBlur={() => touch('tags')}
              error={Boolean(errorFor('tags'))} helperText={errorFor('tags') ?? 'Comma-separated, e.g. project-management'} />
            {teamsFailed ? (
              <TextField id="mcp-entry-owner" label="Owner team" variant="outlined" value={fields.owner} onChange={set('owner')} onBlur={() => touch('owner')}
                error={Boolean(errorFor('owner'))} helperText={errorFor('owner') ?? 'Couldn’t load teams — type the team name'} />
            ) : (
              <Autocomplete<TeamOption>
                options={ownerOptions} loading={teamsLoading} value={ownerOptions.find(team => team.value === fields.owner) ?? null}
                getOptionLabel={option => option.org ? `${option.org}/${option.name}` : option.value}
                getOptionSelected={(option, value) => option.value === value.value}
                filterOptions={filterTeams}
                PopperComponent={OwnerTeamPopper}
                renderOption={option => <Box display="flex" alignItems="center" width="100%" title={option.org ? `${option.org}/${option.name}` : option.name}>
                  <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0, flex: 1 }}>{option.label}{option.label !== option.name && ` · ${option.name}`}</span>
                  {option.org && <Typography component="span" variant="caption" color="textSecondary" style={{ whiteSpace: 'nowrap', flexShrink: 0, marginLeft: 12, fontSize: '0.75rem' }}>{option.org}</Typography>}
                </Box>}
                onChange={(_event, value) => {
                  setFields(previous => ({ ...previous, owner: value?.value ?? '' }));
                  touch('owner');
                  clearServerError('owner');
                }}
                renderInput={params => <TextField {...params} label="Owner team" variant="outlined"
                  onBlur={() => touch('owner')} error={Boolean(errorFor('owner'))} helperText={errorFor('owner')} />}
              />
            )}
          </div>
          <fieldset style={{ border: 0, margin: 0, padding: 0 }}>
            <legend><Typography component="span" variant="subtitle2">Clients</Typography></legend>
            <Typography className={classes.attest}>Tick a new client only if you added the server, signed in, and made one real tool call that returned data.</Typography>
            <div className={classes.clients}>
              {MCP_CLIENT_IDS.map(id => (
                <FormControlLabel key={id} label={<>{CLIENT_LABELS[id]}{editing && previouslyTested.includes(id) && <Typography component="span" color="textSecondary" variant="caption"> · previously tested</Typography>}</>} control={<Checkbox checked={tested.includes(id)}
                  onChange={(_event, checked) => { setTested(previous => checked ? [...previous, id] : previous.filter(client => client !== id)); touch('testedClients'); clearServerError('testedClients'); }} />} />
              ))}
            </div>
            {errorFor('testedClients') && <FormHelperText error>{errorFor('testedClients')}</FormHelperText>}
          </fieldset>
          <RadioGroup row aria-label="Lifecycle" value={fields.lifecycle} onChange={(_event, value) => setFields(previous => ({ ...previous, lifecycle: value as EntryFields['lifecycle'] }))}>
            <FormControlLabel value="production" control={<Radio />} label="Production" />
            <FormControlLabel value="experimental" control={<Radio />} label="Experimental" />
          </RadioGroup>
          <details className={classes.preview} onToggle={event => setPreviewOpen((event.target as HTMLDetailsElement).open)}>
            <summary onClick={event => setPreviewOpen(!(event.currentTarget.parentElement as HTMLDetailsElement).open)}>Preview the files</summary>
            {!valid && <Typography className={classes.attest}>Complete the form to preview.</Typography>}
            {valid && preview?.map(file => <div key={file.path}><Typography component="div" variant="caption">{file.path}</Typography><pre>{file.content}</pre></div>)}
          </details>
          <Box className={classes.actions}>
            <Button onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="contained" color="primary" disabled={submitting || banner?.kind === 'pending'}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}>Open pull request</Button>
          </Box>
        </form>
      )}
    </Dialog>
  );
};
